/**
 * Dashboard Page
 * Job selection with checkboxes and automation
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  TextField,
  MenuItem,
  Select,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WorkIcon from '@mui/icons-material/Work';
import ImageIcon from '@mui/icons-material/Image';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import SyncIcon from '@mui/icons-material/Sync';
import AppLayout from '../components/Layout/AppLayout';
import Toast from '../components/Common/Toast';
import ConfirmDialog from '../components/Common/ConfirmDialog';
import api, { etlAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const firstName = user?.full_name?.split(' ')[0] || '';
  // Load cached data from sessionStorage on mount
  const getCachedData = () => {
    try {
      const cached = sessionStorage.getItem('dashboardData');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  };

  const [dashboardData, setDashboardData] = useState(getCachedData());
  const [loading, setLoading] = useState(!getCachedData());
  const [error, setError] = useState('');
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [platforms, setPlatforms] = useState({
    linkedin: true,
    facebook: false,
    instagram: false,
    external_flyer: false,
  });
  const [imageStyle, setImageStyle] = useState('real_people');
  const [submitting, setSubmitting] = useState(false);
  // Server-side search + pagination
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0); // 0-indexed for MUI TablePagination
  const [rowsPerPage] = useState(100);
  // Client-side fine-grain filters (applied within current page)
  const [filters, setFilters] = useState({
    requisition_number: '',
    requisition_title: '',
    department: '',
    location: '',
    status: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [existingDesignsDialogOpen, setExistingDesignsDialogOpen] = useState(false);
  const [existingDesignsData, setExistingDesignsData] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [proceedToGenerate, setProceedToGenerate] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmStartOpen, setConfirmStartOpen] = useState(false);
  const [etlRunning, setEtlRunning] = useState(false);
  const [etlJustDone, setEtlJustDone] = useState(false);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Debounce search: wait 400ms after user stops typing before firing API call
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  // On mount: check if ETL is already running (e.g. triggered by login)
  useEffect(() => {
    etlAPI.status().then((data) => {
      if (data.running) setEtlRunning(true);
    }).catch(() => {});
  }, []);

  // While ETL is running: poll every 3s, refresh table when it finishes
  useEffect(() => {
    if (!etlRunning) return;
    const pollId = setInterval(async () => {
      try {
        const data = await etlAPI.status();
        if (!data.running) {
          setEtlRunning(false);
          setEtlJustDone(true);
          // Instantly update last_sync from ETL status, then full silent refresh
          if (data.last_updated) {
            setDashboardData(prev => prev ? {
              ...prev,
              stats: { ...prev.stats, last_sync: data.last_updated }
            } : prev);
          }
          // Tell AppLayout sidebar to update with the real completed timestamp
          window.dispatchEvent(new CustomEvent('etl:completed', {
            detail: { last_sync: data.last_updated || new Date().toISOString() }
          }));
          fetchDashboard(true, page, debouncedSearch);
          setTimeout(() => setEtlJustDone(false), 3000);
        }
      } catch (_) {}
    }, 3000);
    return () => clearInterval(pollId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etlRunning]);

  const handleRefreshETL = async () => {
    try {
      await etlAPI.refresh();
      setEtlRunning(true);
      setEtlJustDone(false);
      // Tell AppLayout sidebar to show "Just now" immediately
      window.dispatchEvent(new CustomEvent('etl:started'));
    } catch (_) {
      showSnackbar('Failed to trigger data refresh', 'error');
    }
  };

  useEffect(() => {
    fetchDashboard(false, page, debouncedSearch);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch]);

  const fetchDashboard = async (silentRefresh = false, pageNum = 0, search = '') => {
    if (!silentRefresh) {
      setLoading(true);
    }
    try {
      const params = new URLSearchParams({ page: pageNum + 1, page_size: rowsPerPage });
      if (search.trim()) params.append('search', search.trim());
      const response = await api.get(`/dashboard/?${params.toString()}`);
      setDashboardData(response.data);
      sessionStorage.setItem('dashboardData', JSON.stringify(response.data));
      setError('');
    } catch (err) {
      if (!silentRefresh) {
        setError('Failed to load dashboard data');
      }
      console.error(err);
    } finally {
      if (!silentRefresh) {
        setLoading(false);
      }
    }
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    // page reset happens in the debounce effect above
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Select all filtered jobs
      const filteredJobIds = filteredJobs.map((job) => job.id);
      setSelectedJobs(filteredJobIds);
    } else {
      setSelectedJobs([]);
    }
  };

  const handleSelectJob = (jobId) => {
    setSelectedJobs((prev) =>
      prev.includes(jobId)
        ? prev.filter((id) => id !== jobId)
        : [...prev, jobId]
    );
  };

  const handleGenerateDesigns = async () => {
    if (selectedJobs.length === 0) {
      showSnackbar('Please select at least one job', 'warning');
      return;
    }

    // Check if any selected job already has designs from any user
    const jobsWithDesigns = selectedJobs
      .map(id => filteredJobs.find(j => j.id === id))
      .filter(j => j && j.any_user_designs_count > 0);

    if (jobsWithDesigns.length > 0 && !proceedToGenerate) {
      // Load existing designs for preview
      setLoadingExisting(true);
      setExistingDesignsDialogOpen(true);
      try {
        const previews = await Promise.all(
          jobsWithDesigns.map(job =>
            api.get(`/history/job/${job.id}`).then(r => r.data)
          )
        );
        setExistingDesignsData(previews);
      } catch (err) {
        console.error('Failed to load existing designs', err);
        setExistingDesignsData([]);
      } finally {
        setLoadingExisting(false);
      }
    } else {
      setProceedToGenerate(false);
      setDialogOpen(true);
    }
  };

  const handleGenerateAnyway = () => {
    setExistingDesignsDialogOpen(false);
    setExistingDesignsData([]);
    setProceedToGenerate(true);
    setDialogOpen(true);
  };

  const handleStartAutomation = async () => {
    const selectedPlatforms = Object.keys(platforms).filter((p) => platforms[p]);

    if (selectedPlatforms.length === 0) {
      showSnackbar('Please select at least one platform', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/automation/bulk/start', {
        job_ids: selectedJobs,
        platforms: selectedPlatforms,
        image_style: imageStyle,
      });

      // Don't clear cache - let polling update silently
      // This prevents full-screen loading when navigating back

      setDialogOpen(false);

      // Trigger immediate refresh to show updated status
      fetchDashboard(true);

      navigate('/history');
    } catch (err) {
      showSnackbar('Failed to start automation: ' + (err.response?.data?.detail || err.message), 'error');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      requisition_number: '',
      requisition_title: '',
      department: '',
      location: '',
      status: '',
    });
  };

  // Get automation status for a job
  const getJobStatus = (job) => {
    // Use the status from the most recent run provided by backend
    return job.most_recent_run_status || 'not_started';
  };

  // Only show full-screen loading if there's no data yet
  if (loading && !dashboardData) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  if (error && !dashboardData) {
    return (
      <AppLayout>
        <Alert severity="error">{error}</Alert>
      </AppLayout>
    );
  }

  const stats = dashboardData?.stats || {};
  const jobs = dashboardData?.jobs || [];
  const pagination = dashboardData?.pagination || { total: jobs.length, page: 1, page_size: rowsPerPage, total_pages: 1 };

  // Normalize search terms: collapse hyphens/underscores to spaces for flexible matching
  const normalizeSearch = (s) => (s || '').toLowerCase().replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim();

  // Parse backend UTC timestamps correctly (backend returns no Z suffix)
  const parseUTC = (dateStr) => {
    if (!dateStr) return new Date(0);
    const s = dateStr.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateStr) ? dateStr : dateStr + 'Z';
    return new Date(s);
  };

  // Client-side fine-grain filters applied within the current page
  const filteredJobs = jobs.filter((job) => {
    const matchesReqNumber = !filters.requisition_number ||
      normalizeSearch(job.requisition_number).includes(normalizeSearch(filters.requisition_number));
    const matchesTitle = !filters.requisition_title ||
      normalizeSearch(job.requisition_title).includes(normalizeSearch(filters.requisition_title));
    const matchesDepartment = !filters.department ||
      normalizeSearch(job.department).includes(normalizeSearch(filters.department));
    const matchesLocation = !filters.location ||
      normalizeSearch(job.location).includes(normalizeSearch(filters.location));
    const matchesStatus = !filters.status ||
      getJobStatus(job) === filters.status;

    return matchesReqNumber && matchesTitle && matchesDepartment &&
           matchesLocation && matchesStatus;
  });

  const statCards = [
    {
      title: 'Total Jobs',
      value: stats.total_jobs || 0,
      icon: <WorkIcon fontSize="large" />,
      color: '#1976d2',
    },
    {
      title: 'Your Designs',
      value: stats.user_designs || 0,
      icon: <ImageIcon fontSize="large" />,
      color: '#9c27b0',
    },
    {
      title: 'Completed Runs',
      value: stats.completed_runs || 0,
      icon: <CheckCircleIcon fontSize="large" />,
      color: '#2e7d32',
    },
  ];

  return (
    <AppLayout>
      <Box>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Select jobs and generate social media designs with AI
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  borderLeft: `4px solid ${stat.color}`,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: `${stat.color}15`,
                    color: stat.color,
                  }}
                >
                  {stat.icon}
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={600}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.title}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Jobs Table */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h5" fontWeight={600}>
              Available Jobs
            </Typography>
            <Chip
              label={`${filteredJobs.length}${filteredJobs.length < pagination.total ? ` of ${pagination.total}` : ''} jobs`}
              color="primary"
              variant="outlined"
            />
            <Tooltip title={etlRunning ? 'Syncing job data from Oracle…' : 'Sync latest jobs from Oracle UCM'}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleRefreshETL}
                  disabled={etlRunning}
                  sx={{
                    color: etlRunning ? 'primary.main' : 'text.secondary',
                    '&:hover': { color: 'primary.main', bgcolor: 'primary.50' },
                    '& svg': etlRunning
                      ? { animation: 'spin 1.2s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }
                      : {},
                  }}
                >
                  <SyncIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search jobs…"
              value={searchText}
              onChange={handleSearchChange}
              sx={{ width: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant={showFilters ? 'contained' : 'outlined'}
              startIcon={showFilters ? <ClearIcon /> : <FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide Filters' : 'Filters'}
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<PlayArrowIcon />}
              onClick={handleGenerateDesigns}
              disabled={selectedJobs.length === 0}
            >
              Generate Designs ({selectedJobs.length})
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedJobs.length === filteredJobs.length && filteredJobs.length > 0}
                    indeterminate={selectedJobs.length > 0 && selectedJobs.length < filteredJobs.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell><strong>Req #</strong></TableCell>
                <TableCell><strong>Job Title</strong></TableCell>
                <TableCell><strong>Department</strong></TableCell>
                <TableCell><strong>Location</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Existing Designs</strong></TableCell>
              </TableRow>
              {showFilters && (
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell padding="checkbox">
                    <Tooltip title="Clear All Filters">
                      <IconButton size="small" onClick={handleClearFilters}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Filter..."
                      value={filters.requisition_number}
                      onChange={(e) => handleFilterChange('requisition_number', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Filter..."
                      value={filters.requisition_title}
                      onChange={(e) => handleFilterChange('requisition_title', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Filter..."
                      value={filters.department}
                      onChange={(e) => handleFilterChange('department', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Filter..."
                      value={filters.location}
                      onChange={(e) => handleFilterChange('location', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      fullWidth
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                      <MenuItem value="not_started">Not Started</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {filteredJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <WorkIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      {jobs.length === 0 ? 'No jobs available' : 'No jobs match the filter criteria'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredJobs.map((job) => {
                  const jobStatus = getJobStatus(job);
                  const statusConfig = {
                    completed: { label: 'Completed', color: 'success' },
                    in_progress: { label: 'In Progress', color: 'warning' },
                    failed: { label: 'Failed', color: 'error' },
                    not_started: { label: 'Not Started', color: 'default' },
                  };
                  const status = statusConfig[jobStatus] || statusConfig.not_started;

                  return (
                  <TableRow
                    key={job.id}
                    hover
                    selected={selectedJobs.includes(job.id)}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleSelectJob(job.id)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedJobs.includes(job.id)}
                        onChange={() => handleSelectJob(job.id)}
                      />
                    </TableCell>
                    <TableCell>{job.requisition_number}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {job.requisition_title}
                      </Typography>
                    </TableCell>
                    <TableCell>{job.department}</TableCell>
                    <TableCell>{job.location}</TableCell>
                    <TableCell>
                      <Chip
                        label={status.label}
                        size="small"
                        color={status.color}
                      />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {job.any_user_designs_count > 0 ? (
                        <Chip
                          label={`${job.any_user_designs_count} designs`}
                          size="small"
                          color="info"
                          variant="outlined"
                          clickable
                          onClick={() => navigate(`/history?job=${job.id}`)}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {pagination.total > rowsPerPage && (
          <TablePagination
            component="div"
            count={pagination.total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
            sx={{ mt: 1 }}
          />
        )}

        {/* Existing Designs Preview Dialog */}
        <Dialog
          open={existingDesignsDialogOpen}
          onClose={() => { setExistingDesignsDialogOpen(false); setExistingDesignsData([]); }}
          maxWidth="md"
          fullWidth
          slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: 'rgba(0,0,0,0.45)' } } }}
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle>
            Existing Designs Found
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              The selected job(s) already have designs generated by your team. Review them below before deciding to generate new ones.
            </Alert>
            {loadingExisting ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              existingDesignsData.map((jobData) => (
                <Box key={jobData.job_id} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {jobData.job_title}
                  </Typography>
                  {jobData.runs.map((run) => (
                    <Paper key={run.run_id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Chip
                          label={run.run_by?.full_name || run.run_by?.email || 'Unknown'}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {run.platforms?.join(', ')} · {parseUTC(run.created_at).toLocaleDateString()}
                        </Typography>
                        <Chip label={`${run.designs?.length || 0} designs`} size="small" variant="outlined" sx={{ ml: 'auto' }} />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {run.designs?.filter(d => d.export_png_url).map((design) => (
                          <Box
                            key={design.id}
                            component="img"
                            src={design.export_png_url}
                            alt={design.platform}
                            onClick={() => { setExistingDesignsDialogOpen(false); navigate(`/history?job=${jobData.job_id}`); }}
                            sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider', cursor: 'pointer', '&:hover': { opacity: 0.8, borderColor: 'primary.main' } }}
                          />
                        ))}
                        {(run.designs?.filter(d => d.export_png_url).length || 0) === 0 && (
                          <Typography variant="caption" color="text.secondary">No preview available</Typography>
                        )}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ))
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setExistingDesignsDialogOpen(false); setExistingDesignsData([]); }}>
              Cancel
            </Button>
            <Button onClick={handleGenerateAnyway} variant="contained" color="warning">
              Generate Anyway
            </Button>
          </DialogActions>
        </Dialog>

        {/* Automation Configuration Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: 'rgba(0,0,0,0.45)' } } }}
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle>Configure Automation</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Jobs: {selectedJobs.length}
              </Typography>

              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                Select Platforms:
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={platforms.linkedin}
                      onChange={(e) => setPlatforms({ ...platforms, linkedin: e.target.checked })}
                    />
                  }
                  label="LinkedIn (5:4)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={platforms.facebook}
                      onChange={(e) => setPlatforms({ ...platforms, facebook: e.target.checked })}
                    />
                  }
                  label="Facebook (1:1)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={platforms.instagram}
                      onChange={(e) => setPlatforms({ ...platforms, instagram: e.target.checked })}
                    />
                  }
                  label="Instagram (4:5)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={platforms.external_flyer}
                      onChange={(e) => setPlatforms({ ...platforms, external_flyer: e.target.checked })}
                    />
                  }
                  label="External Flyer (1:1)"
                />
              </FormGroup>

              <FormControl component="fieldset" sx={{ mt: 3 }}>
                <FormLabel component="legend">AI Image Style</FormLabel>
                <RadioGroup
                  value={imageStyle}
                  onChange={(e) => setImageStyle(e.target.value)}
                >
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <img
                          src="/samples/people-sample.png"
                          alt="People style sample"
                          style={{ width: '200px', height: '200px', objectFit: 'cover', borderRadius: '4px' }}
                        />
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                          Sample: Professional team photo
                        </Typography>
                      </Box>
                    }
                    placement="right"
                    arrow
                  >
                    <FormControlLabel
                      value="real_people"
                      control={<Radio />}
                      label="People - Professional team photos"
                    />
                  </Tooltip>
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <img
                          src="/samples/abstract-sample.png"
                          alt="Abstract style sample"
                          style={{ width: '200px', height: '200px', objectFit: 'cover', borderRadius: '4px' }}
                        />
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                          Sample: Apparel & textile close-up
                        </Typography>
                      </Box>
                    }
                    placement="right"
                    arrow
                  >
                    <FormControlLabel
                      value="abstract"
                      control={<Radio />}
                      label="Abstract - Apparel & textile close-ups"
                    />
                  </Tooltip>
                </RadioGroup>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => setConfirmStartOpen(true)}
              variant="contained"
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={24} /> : 'Start Automation'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      <ConfirmDialog
        open={confirmStartOpen}
        title="Start Automation?"
        message={`This will generate designs for ${selectedJobs.length} job${selectedJobs.length !== 1 ? 's' : ''} across the selected platforms. Continue?`}
        confirmLabel="Yes, Start"
        cancelLabel="Cancel"
        confirmColor="primary"
        onConfirm={() => { setConfirmStartOpen(false); handleStartAutomation(); }}
        onClose={() => setConfirmStartOpen(false)}
      />

      <Toast
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      />

      {/* Bottom-left ETL status indicator */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 28,
          left: 28,
          zIndex: 1400,
          transition: 'opacity 0.35s ease, transform 0.35s ease',
          opacity: etlRunning || etlJustDone ? 1 : 0,
          transform: etlRunning || etlJustDone ? 'translateY(0)' : 'translateY(10px)',
          pointerEvents: 'none',
        }}
      >
        <Paper
          elevation={4}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2.5,
            py: 1.5,
            borderRadius: 3,
            borderLeft: '4px solid',
            borderColor: etlJustDone ? 'success.main' : 'primary.main',
            bgcolor: 'background.paper',
            minWidth: 230,
          }}
        >
          {etlRunning && (
            <CircularProgress size={16} thickness={5} sx={{ color: 'primary.main', flexShrink: 0 }} />
          )}
          {etlJustDone && !etlRunning && (
            <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }} />
          )}
          <Box>
            <Typography variant="body2" fontWeight={600} color={etlJustDone && !etlRunning ? 'success.main' : 'text.primary'} lineHeight={1.2}>
              {etlRunning ? 'Syncing job data…' : 'Jobs updated'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {etlRunning ? 'Fetching latest from Oracle UCM' : 'Table refreshed with latest data'}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </AppLayout>
  );
}
